/*
 * IndexedDBStorage — persistance locale (JSON et binaires)
 *
 * Interface identique aux anciens storages :
 *   open( dir, name )              → Deferred<Blob>
 *   read( dir, name )              → Deferred<string>
 *   write( dir, name, blob )       → Deferred<Blob>
 *   remove( dir, name )            → Deferred
 *
 * Les données sont stockées dans IndexedDB (base "mares", store "files")
 * avec la clé composée "dir/name".
 */

var DB_NAME    = 'mares';
var DB_VERSION = 1;
var STORE_NAME = 'files';

// Connexion unique réutilisée
var _db = null;

function _connect()
{
	if( _db )
	{
		return $.Deferred().resolve( _db );
	}

	var deferred = $.Deferred();

	var request = indexedDB.open( DB_NAME, DB_VERSION );

	request.onupgradeneeded = function( evt )
	{
		var db = evt.target.result;
		if( !db.objectStoreNames.contains( STORE_NAME ) )
		{
			db.createObjectStore( STORE_NAME ); // keyPath manuel via put(value, key)
		}
	};

	request.onsuccess = function( evt )
	{
		_db = evt.target.result;

		// En cas de mise à jour de version en arrière-plan
		_db.onversionchange = function()
		{
			_db.close();
			_db = null;
		};

		deferred.resolve( _db );
	};

	request.onerror = function( evt )
	{
		deferred.reject( 'IndexedDB: impossible d\'ouvrir la base (' + evt.target.error + ')' );
	};

	request.onblocked = function()
	{
		deferred.reject( 'IndexedDB: ouverture bloquée par un autre onglet' );
	};

	return deferred;
}

function _key( dir, name )
{
	return dir + '/' + name;
}


export default {

	// Retourne le Blob stocké
	open: function( dir, name )
	{
		var key      = _key( dir, name );
		var deferred = $.Deferred();

		console.log( 'IndexedDBStorage.open', key, '…' );

		_connect().done( function( db )
		{
			var tx      = db.transaction( STORE_NAME, 'readonly' );
			var store   = tx.objectStore( STORE_NAME );
			var request = store.get( key );

			request.onsuccess = function()
			{
				var value = request.result;

				if( value === undefined )
				{
					console.warn( 'IndexedDBStorage.open', key, 'not found' );
					deferred.reject( key + ' not found' );
					return;
				}

				// Valeur stockée : Blob ou { data: base64string, type: string }
				var blob = ( value instanceof Blob ) ? value : _base64ToBlob( value.data, value.type );
				blob.name = name;
				console.log( 'IndexedDBStorage.open', key, 'ok', blob.size );
				deferred.resolve( blob );
			};

			request.onerror = function()
			{
				deferred.reject( 'IndexedDBStorage.open error: ' + request.error );
			};
		}).fail( deferred.reject );

		return deferred;
	},


	// Écrit un Blob (ou une string) dans IndexedDB
	write: function( dir, name, blob )
	{
		var key      = _key( dir, name );
		var deferred = $.Deferred();

		console.log( 'IndexedDBStorage.write', key, '…' );

		// Normalise en Blob si on reçoit une string
		if( !( blob instanceof Blob ) )
		{
			blob = new Blob( [ blob ], { type: 'text/plain' } );
		}

		_connect().done( function( db )
		{
			var tx    = db.transaction( STORE_NAME, 'readwrite' );
			var store = tx.objectStore( STORE_NAME );

			// On stocke directement le Blob (IndexedDB supporte les Blobs nativement)
			var request = store.put( blob, key );

			request.onsuccess = function()
			{
				console.log( 'IndexedDBStorage.write', key, 'ok' );
				deferred.resolve( blob );
			};

			request.onerror = function()
			{
				// Fallback : certains navigateurs anciens ne supportent pas le stockage
				// direct de Blob. On convertit en base64.
				_blobToBase64( blob ).done( function( b64 )
				{
					var tx2    = db.transaction( STORE_NAME, 'readwrite' );
					var store2 = tx2.objectStore( STORE_NAME );
					var req2   = store2.put( { data: b64, type: blob.type }, key );

					req2.onsuccess = function()
					{
						console.log( 'IndexedDBStorage.write (base64 fallback)', key, 'ok' );
						deferred.resolve( blob );
					};
					req2.onerror = function()
					{
						deferred.reject( 'IndexedDBStorage.write error: ' + req2.error );
					};
				}).fail( deferred.reject );
			};
		}).fail( deferred.reject );

		return deferred;
	},


	// Supprime une entrée
	remove: function( dir, name )
	{
		var key      = _key( dir, name );
		var deferred = $.Deferred();

		console.log( 'IndexedDBStorage.remove', key, '…' );

		_connect().done( function( db )
		{
			var tx      = db.transaction( STORE_NAME, 'readwrite' );
			var store   = tx.objectStore( STORE_NAME );
			var request = store.delete( key );

			request.onsuccess = function()
			{
				console.log( 'IndexedDBStorage.remove', key, 'ok' );
				deferred.resolve( key );
			};

			request.onerror = function()
			{
				deferred.reject( 'IndexedDBStorage.remove error: ' + request.error );
			};
		}).fail( deferred.reject );

		return deferred;
	}

};


// — Utilitaires privés —

function _blobToBase64( blob )
{
	var deferred = $.Deferred();
	var fr       = new FileReader();

	fr.readAsDataURL( blob );
	fr.onload  = function()
	{
		// Retire le préfixe "data:type;base64,"
		var prefix = 'data:' + blob.type + ';base64,';
		deferred.resolve( fr.result.substr( prefix.length ) );
	};
	fr.onerror = function() { deferred.reject( 'FileReader error' ); };
	fr.onabort = function() { deferred.reject( 'FileReader aborted' ); };

	return deferred;
}

function _base64ToBlob( b64, type )
{
	var binary = atob( b64 );
	var bytes  = new Uint8Array( binary.length );
	for( var i = 0; i < binary.length; i++ )
	{
		bytes[i] = binary.charCodeAt( i );
	}
	return new Blob( [ bytes ], { type: type || 'application/octet-stream' } );
}
